import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import ParaList from "@/components/Reader/ParaList/ParaList";
import { getReaderFirstVisibleId } from "@/shared/utils/reader/reader";
import { PLAYER_EVENTS, useAudioContext } from "@/shared/contexts/AudioContext";
import { ReaderArrowIcon } from "../../../assets/ReaderIcons";
import { selectorSystemIsTopPanelVisible } from "@/shared/store/system/selectors";
import { Button, Typography } from "@/components/index";
import ReaderModeOnlyElement from "@/shared/components/ReaderModeOnlyElement";
import { isParaHeader } from "@/shared/utils/reader/reader";
import { calcPercentage, clutch, isNumberBetweenEquals } from "@/shared/utils/number/number";
import DictionaryPopup from "./DictionaryPopup/DictionaryPopup";
import TranslationsPopup from "./TranslationsPopup/TranslationsPopup";
import { useAuthContext } from "@/contexts/auth";
import {
  CN_EL_ARROW_PLAYER,
  CN_EL_READER_MODE,
  CN_EL_SWIPE_ARROWS,
  layoutIds,
} from "@/components/Reader/Panel.constants";
import { DATA_ATTR_PANEL_INDEX, DATA_ATTR_PARA_ID } from "@/shared/utils/reader/constants";
import useReaderPanelLink from "@/shared/hooks/reader/useReaderPanelLink";
import SwipeArrows from "@/shared/components/SwipeArrows/SwipeArrows";
import useMediaQuery, { MEDIA_QUERIES } from "@/shared/hooks/useMediaQuery";
import useRefData from "@/shared/hooks/useRefData";
import { selectorSearchResults } from "@/shared/store/search/selectors";
import { CONTENT_CLASSES } from "@/shared/utils/content/content.constants";
import { useReaderContext } from "@/components/Reader/Reader";
import { systemActions } from "@/store/actions";
import useHighlightContainingMarkNoteElements from "@/shared/hooks/reader/useHighlightContainingMarkNoteElements";
import { getClosestNoteElement } from "@/shared/utils/reader/reader-notes";
import { useWhatInputIsLastPointerMouse } from "@/shared/what-input/useWhatInput";
import { clearSelection, getSelectionCrossBrowser } from "@/shared/utils/selection";
import { useGlobalSwipeable } from "@/contexts/swipeable";
import { isBookPermissionAuthOK } from "@/shared/utils/content/content";
import { makeChapterUrl } from "@/utils/routes/routes";
import { usePagePropsContext } from "@/contexts/pageProps";
import Scroll from "@/shared/components/Scroll/Scroll";
import useReaderLiveReading from "@/shared/hooks/reader/useReaderLiveReading";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import {
  _deprecated_calculateFitPosition,
  _deprecated_calculateMaxSizes,
  isEventDoubleClick,
} from "@/shared/utils/dom";
import { usePopup } from "@/shared/hooks/usePopup";
import { EL_ID_TOP_PANEL } from "@/components/TopPanel/TopPanel.constants";
import {
  getParaIdByParaElement,
  getClosestParaWrapElement,
} from "@/shared/utils/reader/reader-content";

// dynamic, to prevent console errors - server content mismatch the client's.
const Loader = dynamic(() => import("@/shared/components/Loader/Loader"), {
  ssr: false,
});

import styles from "./reader.module.scss";
import useIsDOMHydratedAfterSSR from "@/hooks/useIsDOMHydratedAfterSSR";

const DELAY_DETECT_FIRST_PARA = 150;
const LOG_TOP_PANEL_VISIBILITY_DEFINER = false;

const Popups = {
  TRANSLATIONS: "TRANSLATIONS",
  DICTIONARY: "DICTIONARY",
};

const Panel = ({
  className,
  data,
  active,
  currentLayout,
  fontSize,
  prevChapter,
  nextChapter,
  index,
  linkActive,
}) => {
  const { isLanding } = usePagePropsContext();
  const {
    readerPlayerHookInstance,
    syncPanels,
    updatePanel,
    updateIndexPanelActive,
    openLink,
    panelsIds,
    linksActive,
    fontSizeFinal,
  } = useReaderContext();
  const id = panelsIds[index];
  const isMainPanel = index === 0;
  const {
    currentPlayingPara,
    stopSyncPlayerAndReader,
    indexPanelInPlayer,
  } = readerPlayerHookInstance;

  const { t } = useTranslation();
  const { book, paraList, paraListLoading, isLoading } = data;
  const dispatch = useDispatch();
  const { isLogin, login } = useAuthContext();
  const { addSwipedListener, removeSwipedListener } = useGlobalSwipeable();
  const { urlOrigin } = usePagePropsContext();
  const isMobileOrTablet = useMediaQuery(MEDIA_QUERIES.untilDesktopAndDesktop);
  const isLastPointerMouse = useWhatInputIsLastPointerMouse();
  const showRefCodes = useSelector(selectorSetting(SETTING.showRefCodes.id));
  const showTranslations = useSelector(selectorSetting(SETTING.showTranslations.id));
  const isDOMHydratedAfterSSR = useIsDOMHydratedAfterSSR();

  const refTimeout = useRef();
  const refTimeout2 = useRef();
  const refTimeout3 = useRef();

  const refSwipeArrows = useRef(null);

  const refLastTouchStartScrollTop = useRef(null);
  const refIsScreenTouched = useRef(false);
  const refTopPanelHeight = useRef(null);

  const [scrollTopValue, setScrollTopValue] = useState(undefined);

  if (isDOMHydratedAfterSSR) {
    const height = document.getElementById(EL_ID_TOP_PANEL)?.offsetHeight;

    if (height) {
      refTopPanelHeight.current = height;
    }
  }

  const isAllowedToggleTopPanel = isDOMHydratedAfterSSR && isMobileOrTablet;
  const isTopPanelCoversPartOfContent =
    isAllowedToggleTopPanel && (index === 0 || (index === 1 && currentLayout === layoutIds.l2v));
  const readerTopEdgeHeightOccupied = isTopPanelCoversPartOfContent ? refTopPanelHeight.current : 0;
  const refReaderTopEdgeHeightOccupied = useRef();
  refReaderTopEdgeHeightOccupied.current = readerTopEdgeHeightOccupied;

  const refIsPreventDetectFirstVisible = useRef(false);

  const refParaIdLastSyncedOnScrollStop = useRef(null);
  const refLastScrollData = useRef({ scrollTop: undefined, chapterId: undefined });
  const refPreventStopSyncReaderAndPlayer = useRef(false);

  const refElArrow = useRef(null);

  const [elNoteActive, setElNoteActive] = useState(null);
  const refScrollbars = useRef(null);
  const elContentWrap = refScrollbars.current?.view;

  const [activePopup, setActivePopup] = useState(undefined);
  const [dictionarySearch, setDictionarySearch] = useState("");

  const isShownMarks = useSelector((state) => state.search.isShownMarks);
  const totalTextSearch = useSelector(selectorSearchResults).total;
  const isTopPanelVisible = useSelector(selectorSystemIsTopPanelVisible);

  const { chapterId, subscribeToPlayerEvents, unsubscribeFromPlayerEvents } = useAudioContext();

  const [listTranslations, setListTranslations] = useState([]);
  const { isShown, anchorEl, openPopup, closePopup } = usePopup();

  const paraListIds = useMemo(() => {
    return paraList?.paras?.map((p) => p.para_id) || [];
  }, [paraList]);

  const indexParaActive = useMemo(() => {
    if (paraList?.paras) {
      return paraList.paras?.findIndex((p) => p.para_id === id);
    }
    return undefined;
  }, [paraList, id]);

  const idParaHighlighted = useMemo(() => {
    const isActiveIdHeader =
      indexParaActive !== undefined && isParaHeader(paraList?.paras?.[indexParaActive]);

    if (isActiveIdHeader) {
      const firstNextNonHeaderPara = paraList?.paras
        ?.slice(indexParaActive + 1)
        .find((p) => !isParaHeader(p));

      if (firstNextNonHeaderPara) {
        return firstNextNonHeaderPara.para_id;
      }
    }

    return id;
  }, [id, paraList, indexParaActive]);

  const percentContentScrolled = elContentWrap
    ? calcPercentage(scrollTopValue, elContentWrap.scrollHeight - elContentWrap.offsetHeight)
    : 0;

  const isAllowedShowParas = isBookPermissionAuthOK(book, isLogin) !== false;

  const isCanShowArrows =
    isMobileOrTablet &&
    (indexParaActive === paraListIds.length - 1 || percentContentScrolled >= 98.5);

  const rectElContentWrap = useMemo(() => {
    return elContentWrap && elContentWrap.getBoundingClientRect();
  }, [elContentWrap]);

  const preventUpdateActiveIdBasedOnFirstVisible = useCallback(() => {
    clearTimeout(refTimeout.current);

    // Prevent detect first visible during {DELAY_DETECT_FIRST_PARA + 100ms} after auto-scroll
    // We detect first visible only on user's scroll action.
    // Otherwise, extra detection is fired when navigate through search results in Reader:
    // e.g. search "god", navigate until there is no scroll height
    // for search item para to be th first in the view.
    refIsPreventDetectFirstVisible.current = true;

    refTimeout.current = setTimeout(() => {
      refIsPreventDetectFirstVisible.current = false;
    }, DELAY_DETECT_FIRST_PARA + 250);
  }, []);

  const { refStepLiveRead, isReadyAndWaitingToLiveRead, isLiveReading } = useReaderLiveReading({
    isEnabled: !!(index === indexPanelInPlayer),

    currentPlayingPara,

    refScrollView: refScrollbars,

    getArrowPosition: () => {
      if (refElArrow.current) {
        return parseFloat(refElArrow.current.style.top);
      }

      return undefined;
    },
    onUpdateArrowPosition: (position) => {
      if (refElArrow.current) {
        refElArrow.current.style.top = position + "px";
      }
    },
    onUpdatePanel: (idActiveNew) => updatePanel(index, idActiveNew),

    onAnimationStopped: preventUpdateActiveIdBasedOnFirstVisible,
  });

  const refData = useRefData({
    isLastPointerMouse,
    elContentWrap,
    isCanShowArrows,
    isAllowedToggleTopPanel,
    isTopPanelVisible,
    isLiveReading,
    id,
    fontSizeFinal,
    currentPlayingPara,
    panelsIds,
    active,
    index,
    chapterId,
    nextChapter,
    prevChapter,
    elNoteActive,
    linksActive,
    paraListIds,
  });

  const preventStopSyncReaderAndPlayer = useCallback(() => {
    clearTimeout(refTimeout3.current);
    refPreventStopSyncReaderAndPlayer.current = true;

    refTimeout3.current = setTimeout(() => {
      refPreventStopSyncReaderAndPlayer.current = false;
    }, 100);
  }, []);

  // TODO: Need to move "PopupContext" of Main app to Next app and use here.
  //  Also, need to move notes logic from "ReaderView.js" to a common hook for these projects.
  const temporary_handleNote = (e) => {
    const elNote = getClosestNoteElement(e.target);

    setElNoteActive(elNote);

    if (elNote) {
      const elNotePopup = elNote.querySelector('[class$="note"]');
      if (elNotePopup) {
        const rectBoundaries = e.currentTarget.getBoundingClientRect();
        const rectElNote = elNote.getBoundingClientRect();

        const { maxWidth, maxHeight } = _deprecated_calculateMaxSizes(rectBoundaries, rectElNote);
        elNotePopup.style.maxWidth = maxWidth + "px";
        elNotePopup.style.maxHeight = maxHeight + "px";

        const rectElNotePopup = elNotePopup.getBoundingClientRect();
        const { top, left } = _deprecated_calculateFitPosition(
          rectBoundaries,
          rectElNote,
          rectElNotePopup.width,
          rectElNotePopup.height,
        );
        elNotePopup.style.top = top + "px";
        elNotePopup.style.left = left + "px";

        return true;
      }
    }

    return false;
  };

  // Fast-fix for touch devices.
  const unhoverElementNote = useCallback(() => {
    const { elNoteActive } = refData.current;
    if (elNoteActive) {
      const elNoteActiveClone = elNoteActive.cloneNode(true);
      elNoteActive.parentNode.replaceChild(elNoteActiveClone, elNoteActive);
      setElNoteActive(null);
    }
  }, []);

  const openLinkByEvent = (e) => {
    const linkData = e.target.dataset.link;

    if (linkData) {
      const elPara = getClosestParaWrapElement(e.target);

      if (elPara) {
        openLink(
          linkData,
          getParaIdByParaElement(elPara),
          e.target.classList.contains(CONTENT_CLASSES.EGW_LINK_BIBLE),
        );
        return true;
      }
    }

    return false;
  };

  const openDictionaryPopupByEvent = (e) => {
    if (isEventDoubleClick(e)) {
      const windowSelection = getSelectionCrossBrowser();
      const selectionText = windowSelection.toString().trim();
      if (selectionText) {
        const range = windowSelection.getRangeAt(0);
        setDictionarySearch(selectionText);
        setActivePopup(Popups.DICTIONARY);

        openPopup({
          detail: {
            target: range,
          },
        });

        return true;
      }
    }

    return false;
  };

  const updatePanelParaIdAndActiveStatusByEvent = (event) => {
    const paraId = getParaIdByParaElement(getClosestParaWrapElement(event.target));

    let isParaIdWasUpdated = false;

    if (active) {
      if (paraId && paraId !== id) {
        updatePanel(index, paraId);
        isParaIdWasUpdated = true;
      }
    } else {
      if (!paraId || paraId === id) {
        updateIndexPanelActive(index);
      } else {
        updatePanel(index, paraId);
        isParaIdWasUpdated = true;
      }
    }

    return {
      isParaIdWasUpdated,
    };
  };

  const handleClick = (e) => {
    const { isParaIdWasUpdated } = updatePanelParaIdAndActiveStatusByEvent(e);

    const isDictionaryPopupWasOpened = openDictionaryPopupByEvent(e);
    if (isDictionaryPopupWasOpened) {
      return;
    }

    const isFootNoteWasOpened = temporary_handleNote(e);
    if (isFootNoteWasOpened) {
      return;
    }

    const isLinkWasOpened = openLinkByEvent(e);
    if (isLinkWasOpened) {
      return;
    }

    if (!isParaIdWasUpdated) {
      setTopPanelVisibility(!isTopPanelVisible, scrollTopValue);

      if (isCanShowArrows && refSwipeArrows.current) {
        refSwipeArrows.current.showArrowsWithAutoHide();
      }
    }
  };

  const handleMouseMove = temporary_handleNote;

  const scroll = useCallback(
    (top, smooth = false) => {
      if (!elContentWrap) {
        return;
      }

      if (smooth) {
        elContentWrap.scrollTo({
          top,
          behavior: "smooth",
        });
      } else {
        elContentWrap.scrollTop = top;
      }
    },
    [elContentWrap],
  );

  const setTopPanelVisibility = (flag) => {
    dispatch(systemActions.setTopPanelVisibility(flag));
  };

  const defineTopPanelVisibilityByEvent = (e) => {
    if (!isAllowedToggleTopPanel) {
      if (LOG_TOP_PANEL_VISIBILITY_DEFINER) {
        console.log("not allowed");
      }
      return;
    }

    const target = e.target;
    const scrollTop = target.scrollTop;
    const height = target.offsetHeight;

    const isScrollDirectionDown = scrollTop > refLastScrollData.current.scrollTop;
    const isRequiredToShowPanel = scrollTop < refTopPanelHeight.current;

    let stateNew;

    if (isRequiredToShowPanel) {
      stateNew = true;
    } else {
      // Prevent toggling Top Panel after user has released the touch,
      // so the panel state won't be changed on a non-user interaction.
      // if (!refIsScreenTouched.current) {
      //   if (LOG_TOP_PANEL_VISIBILITY_DEFINER) {
      //     console.log("not touched");
      //   }
      //   return;
      // }
      // ========================================================================================

      // Prevent toggling Top Panel if it's a scroll debounce.
      const isScrollDebouncing = scrollTop < 0 || scrollTop > height + target.scrollHeight;
      if (isScrollDebouncing) {
        if (LOG_TOP_PANEL_VISIBILITY_DEFINER) {
          console.log("is debouncing");
        }
        return;
      }
      // ========================================================================================

      stateNew = !isScrollDirectionDown;

      // // Prevent toggle Top Panel if scrolled only a little.
      const SIZE_SCROLLED_ENOUGH = 24;
      const scrolledDistance = scrollTop - refLastTouchStartScrollTop.current;

      const isScrolledEnough = isScrollDirectionDown
        ? scrolledDistance >= SIZE_SCROLLED_ENOUGH
        : scrolledDistance <= -SIZE_SCROLLED_ENOUGH;

      if (!isScrolledEnough) {
        if (LOG_TOP_PANEL_VISIBILITY_DEFINER) {
          console.log("not far away to show: ", scrolledDistance);
        }
        return;
      }
      // // ========================================================================================
    }

    if (stateNew !== isTopPanelVisible) {
      setTopPanelVisibility(stateNew, scrollTop);
    }
  };

  const goToPrevChapter = useCallback(() => {
    const { prevChapter, index } = refData.current;

    if (prevChapter?.para_id) {
      updatePanel(index, prevChapter.para_id, {
        updatePlayerChapterToo: true,
      });
    }
  }, [updatePanel]);

  const goToNextChapter = useCallback(() => {
    const { nextChapter, index } = refData.current;

    if (nextChapter?.para_id) {
      updatePanel(index, nextChapter.para_id, {
        updatePlayerChapterToo: true,
      });
    }
  }, [updatePanel]);

  const handleClickTranslate = (event, { translations }) => {
    event.preventDefault();
    event.stopPropagation();

    openPopup(event);
    setListTranslations(translations);
    setActivePopup(Popups.TRANSLATIONS);
  };

  const handleClickAnchor = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    dispatch(
      systemActions.actionCopy(urlOrigin + makeChapterUrl(item.para_id), {
        successMessage: t("linkCopied"),
        errorMessage: t("linkNotCopied"),
      }),
    );
  };

  const handleHidePopup = () => {
    clearSelection();
    closePopup();
    setActivePopup(undefined);
  };

  const handleTouchStart = () => {
    if (!isLastPointerMouse) {
      refIsScreenTouched.current = true;
      refLastTouchStartScrollTop.current = scrollTopValue;
    } else {
      if (refIsScreenTouched.current) {
        refIsScreenTouched.current = false;
      }

      if (refLastTouchStartScrollTop.current) {
        refLastTouchStartScrollTop.current = null;
      }
    }
  };
  const handleTouchEnd = () => {
    if (!isLastPointerMouse) {
      refIsScreenTouched.current = false;
    } else {
      if (refIsScreenTouched.current) {
        refIsScreenTouched.current = false;
      }

      if (refLastTouchStartScrollTop.current) {
        refLastTouchStartScrollTop.current = null;
      }
    }
  };

  /* ====================== Scroll hooks =================== */
  // Handles view scroll event.
  const refSyncPanels = useRef(syncPanels);
  refSyncPanels.current = syncPanels;
  const refDefineTopPanelVisibilityByScrollTop = useRef();
  refDefineTopPanelVisibilityByScrollTop.current = defineTopPanelVisibilityByEvent;
  useEffect(() => {
    if (!elContentWrap) {
      return undefined;
    }

    let idTimerDetectFirstPara;
    let idTimerStart, eventTimestampStart;

    const handleScroll = (e) => {
      // ============== BEGIN {eventTimestampStart} logic =============
      const eventTimeStamp = e.timeStamp;

      if (!idTimerStart) {
        eventTimestampStart = eventTimeStamp;
      }

      clearTimeout(idTimerStart);
      idTimerStart = setTimeout(() => {
        idTimerStart = undefined;
      }, 500);
      // ================ END {eventTimestampStart} logic =================

      unhoverElementNote();

      const { isLiveReading, chapterId, active } = refData.current;

      const scrollTop = e.target.scrollTop;

      if (
        isLiveReading &&
        !refPreventStopSyncReaderAndPlayer.current &&
        refLastScrollData.current.scrollTop !== undefined &&
        refLastScrollData.current.chapterId === chapterId &&
        refStepLiveRead.current &&
        Math.abs(refLastScrollData.current.scrollTop - scrollTop) >
          Math.max(refStepLiveRead.current * 1.6, 4) &&
        global.window?.document?.hasFocus()
      ) {
        stopSyncPlayerAndReader();
      }

      if (!refData.current.isLiveReading) {
        if (!refIsPreventDetectFirstVisible.current) {
          const { index } = refData.current;

          // Prevent change active panel for instant-scroll,
          // e.g. split is instant-scrolled after top panel shown/hidden and after that
          // the last (2nd) split always becomes active,
          // because scroll events order is always – 1st split, then 2nd split.
          if (eventTimeStamp - eventTimestampStart > 100) {
            if (!active) {
              updateIndexPanelActive(index);
            }
          }
          // ====================================================================================

          refDefineTopPanelVisibilityByScrollTop.current(e);
        }

        if (refData.current.isCanShowArrows && refSwipeArrows.current) {
          refSwipeArrows.current.showArrowsWithAutoHide();
        }

        if (active) {
          clearTimeout(idTimerDetectFirstPara);

          idTimerDetectFirstPara = setTimeout(() => {
            if (!refIsPreventDetectFirstVisible.current) {
              const { id, currentPlayingPara, paraListIds, index, fontSizeFinal } = refData.current;

              const firstVisiblePara =
                getReaderFirstVisibleId(
                  elContentWrap,
                  false,
                  refReaderTopEdgeHeightOccupied.current,
                ) || paraListIds[paraListIds.length - 1];
              // ==== SPECIAL CASE: Don't update active para ====
              // ==== if it's in the view AND it's in the player ====
              let isPreventUpdateActivePara = false;

              if (currentPlayingPara && currentPlayingPara.id === id) {
                const elParaActive = elContentWrap.querySelector(`[${DATA_ATTR_PARA_ID}="${id}"]`);

                if (elParaActive) {
                  const elParaActiveTop = elParaActive.offsetTop + fontSizeFinal;
                  const elParaActiveBottom =
                    elParaActiveTop + elParaActive.offsetHeight - fontSizeFinal;

                  const viewTop = elContentWrap.scrollTop;
                  const viewBottom = viewTop + elContentWrap.clientHeight;

                  if (
                    isNumberBetweenEquals(
                      elParaActiveTop,
                      viewTop,
                      viewBottom - fontSizeFinal * 1.3,
                    ) ||
                    isNumberBetweenEquals(
                      elParaActiveBottom,
                      viewTop,
                      viewBottom - fontSizeFinal * 1.3,
                    )
                  ) {
                    isPreventUpdateActivePara = true;
                  }
                }
              }
              // ========================================================

              if (firstVisiblePara) {
                if (!isPreventUpdateActivePara) {
                  updatePanel(index, firstVisiblePara);
                }

                refParaIdLastSyncedOnScrollStop.current = firstVisiblePara;
                refSyncPanels.current(firstVisiblePara);
              }
            }
          }, DELAY_DETECT_FIRST_PARA);
        }
      }

      setScrollTopValue(scrollTop);
      refLastScrollData.current = {
        scrollTop,
        chapterId: refData.current.chapterId,
      };
    };

    elContentWrap.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(idTimerDetectFirstPara);
      elContentWrap.removeEventListener("scroll", handleScroll);
    };
  }, [
    isMainPanel,
    elContentWrap,
    stopSyncPlayerAndReader,
    updatePanel,
    unhoverElementNote,
    updateIndexPanelActive,
  ]);

  // Scrolls to {id} on location chapter change.
  const refIsFirstTime = useRef(true);
  useEffect(() => {
    if (!elContentWrap || !id || isLiveReading || isLoading) {
      return;
    }

    const elCurrentPara = elContentWrap.querySelector(`[${DATA_ATTR_PARA_ID}='${id}']`);

    if (elCurrentPara) {
      if (refIsFirstTime.current) {
        preventUpdateActiveIdBasedOnFirstVisible();
        scroll(elCurrentPara.offsetTop);
      } else {
        const viewTop = elContentWrap.scrollTop;
        const viewBottom = viewTop + elContentWrap.offsetHeight;

        const elemTop = elCurrentPara.offsetTop;
        const elemBottom = elemTop + elCurrentPara.offsetHeight;

        if (
          active
            ? !isNumberBetweenEquals(elemTop, viewTop, viewBottom) &&
              !isNumberBetweenEquals(elemBottom, viewTop, viewBottom) &&
              !(viewTop >= elemTop && viewBottom <= elemBottom)
            : !isNumberBetweenEquals(elemTop, viewTop, viewBottom) ||
              !isNumberBetweenEquals(elemBottom, viewTop, viewBottom)
        ) {
          preventUpdateActiveIdBasedOnFirstVisible();
          scroll(elCurrentPara.offsetTop);
        }
      }
    } else {
      scroll(0);
    }

    refIsFirstTime.current = false;
  }, [
    isLoading,
    elContentWrap,
    id,
    scroll,
    preventUpdateActiveIdBasedOnFirstVisible,
    updatePanel,
    index,
  ]);
  /* ====================================================== */

  // Sync panels on location chapter change.
  useEffect(() => {
    if (!paraList?.paras?.length || !id || isLiveReading) {
      return;
    }

    if (isMainPanel && refParaIdLastSyncedOnScrollStop.current !== id) {
      syncPanels(id);
    }
  }, [elContentWrap, paraList, id, isMainPanel]);
  /* ====================================================== */

  // Handle global swipe.
  useEffect(() => {
    const listener = (e) => {
      if (getSelectionCrossBrowser().toString()) {
        // prevent swipe handling if selecting the text.
        return;
      }

      if (e.absX > clutch(window.innerWidth / 5, 75, 150)) {
        if (e.dir === "Left") {
          goToNextChapter();
        }

        if (e.dir === "Right") {
          goToPrevChapter();
        }
      }
    };

    addSwipedListener(listener);

    return () => {
      removeSwipedListener(listener);
    };
  }, [addSwipedListener, removeSwipedListener, goToNextChapter, goToPrevChapter]);

  // {preventStopSyncReaderAndPlayer} on player skip (+/- 10 seconds)
  useEffect(() => {
    const listener = ({ event }) => {
      if (event === PLAYER_EVENTS.SKIP) {
        preventStopSyncReaderAndPlayer();
      }
    };

    subscribeToPlayerEvents(listener);

    return () => {
      unsubscribeFromPlayerEvents(listener);
    };
  }, [preventStopSyncReaderAndPlayer]);

  // Clean up timeouts.
  useEffect(() => {
    return () => {
      clearTimeout(refTimeout.current);
      clearTimeout(refTimeout2.current);
      clearTimeout(refTimeout3.current);
    };
  }, []);

  useReaderPanelLink(
    elContentWrap,
    linkActive,
    "link-selected",
    true,
    preventUpdateActiveIdBasedOnFirstVisible,
  );

  useHighlightContainingMarkNoteElements(elContentWrap, paraList);

  return (
    <div
      className={classNames(styles["root"], className, {
        [styles["live-reading"]]: isReadyAndWaitingToLiveRead || isLiveReading,
        [styles["ready-syncing-with-player"]]: isReadyAndWaitingToLiveRead,
        [styles["syncing-with-player"]]: isLiveReading,
      })}>
      <Scroll
        ref={refScrollbars}
        {...{ [DATA_ATTR_PANEL_INDEX]: index }}
        renderViewClassName={classNames(
          styles["reader-list"],
          "reader-content-wrap",
          book?.folder_color_group,
          {
            // hide marks if no results to not cut the highlights of the prev search, that had results
            [styles["reader-panel-hide-marks"]]: !isShownMarks || !totalTextSearch,
            [styles["passive-reader"]]: !active,
            active: active,
            [styles["is-loading"]]: isLoading,
            [styles["disable-interaction"]]: isLoading && paraListIds.length,
          },
        )}
        isRenderViewFocusable
        style={{ fontSize: fontSize + "px" }}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}>
        {!isAllowedShowParas && (
          <div className={styles["reader-buy-book"]}>
            <p>{t("plsLoginToSeeBookContent")}</p>
            <Button onClick={login}>{t("signIn")}</Button>
          </div>
        )}

        {!(paraList || paraListLoading) && isLoading && isAllowedShowParas && (
          <Loader align="flex-center" />
        )}
        {isAllowedShowParas && (paraList || paraListLoading) && (
          <>
            <SwipeArrows
              ref={refSwipeArrows}
              className={classNames(styles["swipe-arrows"], CN_EL_SWIPE_ARROWS)}
              isCanShowArrowLeft={!!prevChapter}
              isCanShowArrowRight={!!nextChapter}
              onClickArrowLeft={goToPrevChapter}
              onClickArrowRight={goToNextChapter}
            />

            <ReaderModeOnlyElement>
              <Typography className={CN_EL_READER_MODE} tag="span" variant="body3">
                ({t("noteBrowserReaderMode")})
              </Typography>
            </ReaderModeOnlyElement>
            <ParaList
              firstParaOffsetTop={readerTopEdgeHeightOccupied}
              showTranslations={book?.book_id === 2017 ? false : showTranslations}
              showRefCodes={showRefCodes}
              activeId={idParaHighlighted}
              items={paraList || paraListLoading}
              onClickTranslate={handleClickTranslate}
              onClickAnchor={handleClickAnchor}
            />

            {activePopup === Popups.DICTIONARY && (
              <DictionaryPopup
                isShown={isShown}
                anchorEl={anchorEl}
                contentWrapElRect={rectElContentWrap}
                onHidePopup={handleHidePopup}
                search={dictionarySearch}
              />
            )}

            {activePopup === Popups.TRANSLATIONS && (
              <TranslationsPopup
                indexPanel={index}
                isShown={isShown}
                anchorEl={anchorEl}
                contentWrapElRect={rectElContentWrap}
                onHidePopup={handleHidePopup}
                listTranslations={listTranslations}
                className={styles["reader__popup-translations__inner"]}
              />
            )}
          </>
        )}
      </Scroll>
      <div className={classNames(styles["arrow-player"], CN_EL_ARROW_PLAYER)} ref={refElArrow}>
        <ReaderArrowIcon />
      </div>
    </div>
  );
};

Panel.propTypes = {
  data: PropTypes.object,
  active: PropTypes.bool,
  activePanel: PropTypes.number,
  showTranslations: PropTypes.bool,
  fontSize: PropTypes.number,
  prevChapter: PropTypes.object,
  nextChapter: PropTypes.object,
  onClick: PropTypes.func,
  isPlayingCurrentBook: PropTypes.bool,
  onScrollContentWrap: PropTypes.func,
};

export default Panel;
